import { useState, useEffect } from 'react';
import { ThemeProvider } from "@mui/styles";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Head from "next/head";
import Script from 'next/script';
import Router from 'next/router';
import getConfig from 'next/config';
import TagManager from 'react-gtm-module';
import NProgress from 'nprogress';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { theme } from '../../utils/theme';
import Navbar from '../components/navbar/navbar.component';
import ErrorNavbar from '../components/navbar/error-navbar.component';
import WhatsappButton from '../components/shared/whatsapp-button.component';
import ErrorPage from './_error';
import useResponsive from '../../utils/responsive';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-dates/lib/css/_datepicker.css';
import '../styles/nprogress.css';
import '../../public/assets/css/main.css';
import '../../public/assets/css/media.css';
import '../styles/style.css';

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

NProgress.configure({ showSpinner: false });

const { publicRuntimeConfig } = getConfig();

const tagManagerArgs = {
    gtmId: publicRuntimeConfig.HOMII_GOOGLE_TAG_MANAGER_ID,
};

const queryClient = new QueryClient();

function HOMiiApp({ Component, pageProps }) {

  const { isMobile, isTablet } = useResponsive();  
  const [zLoaded, setZLoaded] = useState(false);
  const [pathName, setPathName] = useState('/');
  const [healthError, setHealthError] = useState(null);
  const [healthChecked, setHealthChecked] = useState(false);

  const mobileOffsetVertical = pathName === '/bookings' ? 7 : 10.5;
  const mobileOffsetHorizontal = pathName === '/bookings' ? -15 : -5;
  const zendeskPositionHorizontal = pathName === '/bookings' && (isMobile || isTablet) ? 'top' : 'bottom';  

  useEffect(() => {

      setPathName(_.get(Router, 'router.pathname'));

      const handleRouteChangeComplete = (url) => {
          // Update pathName whenever the route changes successfully
          setPathName(Router.router.pathname);
      };

      // Listen for route changes
      Router.events.on('routeChangeComplete', handleRouteChangeComplete);      

      const jssStyles = document.querySelector('#jss-server-side');
      if (jssStyles && jssStyles.parentNode) {
        jssStyles.parentNode.removeChild(jssStyles);
      }

      TagManager.initialize(tagManagerArgs);

      const checkHealth = async () => {
        try {
          const isProd = publicRuntimeConfig.HOMII_APP_ENV === 'production';
          const baseUrlApp = isProd
            ? publicRuntimeConfig.HOMII_APP_PROD_URL
            : publicRuntimeConfig.HOMII_APP_UAT_URL;          
          const response = await fetch(`${baseUrlApp}/City/all`);
          if (!response.ok) {
            setHealthError(response.status);
            return;            
          }
        } catch (error) {
          setHealthError(500);
        }finally{
          setHealthChecked(true);
        }
      };

      checkHealth();

      // Clean up the event listener when the component unmounts
      return () => {
          Router.events.off('routeChangeComplete', handleRouteChangeComplete);
      };      

  }, []);  

  useEffect(() => {
    // Poll until the widget is available
    const interval = setInterval(() => {
      if (typeof zE !== "undefined") {
        clearInterval(interval);
        setZLoaded(true);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!zLoaded) return;
    let timer = null;
    if (publicRuntimeConfig.HOMII_FEATURE_ZENDESK) {
      // Hide initially
      zE("webWidget", "hide");
      // Show after 14 seconds
      timer = setTimeout(() => {
        zE("webWidget", "show");
      }, 14000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [zLoaded]);

  const renderNavbar = () => {   
    switch (pathName) {
        case '/find-a-space':
            return <Navbar shouldScroll  />;
        case '/room/[roomTypeDescription]/[roomTypeId]':
            return <Navbar shouldScroll isRoomPage  />;
        case '/bookings':
            return <Navbar shouldScroll isBookingsPage  />;
        case '/process/[processName]/[process]':
            return null; 
        case '/404':
          return <ErrorNavbar />                        
        default:
            return <Navbar  />;
    }
  };

  const renderContent = () => {
    const isProd = process.env.NODE_ENV === 'production';
    const baseUrlApp = isProd
      ? publicRuntimeConfig.HOMII_APP_PROD_URL
      : publicRuntimeConfig.HOMII_APP_UAT_URL; 

    return (
      <>
        {renderNavbar()}
        <Component {...pageProps} baseUrlApp={baseUrlApp} />
        <WhatsappButton />
      </>
    );
  };

  const renderError = () => {
      return (
          <>              
              <ErrorPage statusCode={healthError} />
          </>
      );
  };

  const renderHommi = () => {
    return (
      <>
        <QueryClientProvider client={queryClient}>
          <Head>
              <Script
                  type="text/javascript"
                  dangerouslySetInnerHTML={{
                      __html: `window.zESettings = {
                          webWidget: {
                              offset: {
                                  mobile: {
                                              horizontal: '${mobileOffsetHorizontal}px',
                                              vertical: '${mobileOffsetVertical}px'
                                          }
                              },
                              position: { horizontal: 'right', vertical: '${zendeskPositionHorizontal}' },
                              }
                  
                      };`,
                  }}
              />
          </Head>
          <ThemeProvider theme={theme}>
              {renderContent()}
          </ThemeProvider>
        </QueryClientProvider> 
      </>
    );
  };

  if (!healthChecked) {
    return null; // or you could show a loading spinner here
  }

  return <> {healthError ? renderError() : renderHommi()} </>;

};

HOMiiApp.propTypes = {
  pageProps: PropTypes.object,
  Component: PropTypes.any,
};

export default HOMiiApp;